import { Component, EventEmitter, OnInit } from '@angular/core';
import { DocumentItemEditComponent, DocumentSetupResult } from '../../../models/document-item-edit-component.interface';
import { Observable, of } from 'rxjs';
import { DocumentDataUpdateInfo } from '../../../models/docuemnt-data-update-info.model';
import { DocumentCardData } from '../../../models/document-card-data.model';
import { DwFileUploadModalOptions, DwFileUploadModalResult, DwMediaRepositoryService, DwRoMediaEntity, DwValidationResult, DwValidationResults } from '@devwareapps/devware-cap';
import { DocumentsRepositoryService } from '../../../services/documents-repository.service';
import { AppMetaData, DocumentChangeLogTypeAllItems, DocumentFolderChangeLogEntity, DocumentItemChangeLogEntity, DocumentItemEntity, DocumentItemStatusAllItems, DocumentOwnerTypeAllItems, DocumentTypeAllItems } from '../../../../../meta-data/app-meta-data.service';
import { map, mergeMap } from 'rxjs/operators';
import { DocumentsManagerService } from '../../../services/documents-manager.service';
import { SheetValidationResult } from '../../../models/sheet-validation-result.model';
import { AdminManagerService } from '../../../../admin/services/admin-manager.service';

@Component({
  selector: 'app-document-edit',
  templateUrl: './document-edit.component.html',
  styleUrls: ['./document-edit.component.scss']
})
export class DocumentEditComponent implements DocumentItemEditComponent, OnInit {

  selectedItem: DocumentItemEntity;
  title: string;

  dataUpdated = new EventEmitter<DocumentDataUpdateInfo>();
  titleUpdated = new EventEmitter<string>();

  documentCardData: DocumentCardData;
  lastFolder: DocumentFolderChangeLogEntity;
  newMedia: DwRoMediaEntity;

  adding = false;

  
  isFileValid: boolean = true;
  fileUploadHasStarted = false;
  fileUploadResult: DwFileUploadModalResult;

  fileUploadOptions : DwFileUploadModalOptions = {
    allowMultiple: false,
    existingFilesCount: 0,
    maxFiles: 1,
    mediaSelectOptions: {
      allowedFileTypes: '.pdf',
      isSecure: false,
    },
    maxFileSizeBytes: 75 * 1024 * 1024, // 75 MB
  };

  setupDefaults: DocumentSetupResult = {
    dialogTitle: 'Document',
    editTitle: 'Want to edit the document?',
    editMessage: 'Enable editing to upload a new version and add notes.',
    editActionText: 'Edit document',
    startInEditMode: false,
    initialTitle: '',
    changeLog: []
  };

  constructor(
    protected dwMediaRepository: DwMediaRepositoryService,
    protected documentRespositoryService: DocumentsRepositoryService,
    protected documentsManagerService: DocumentsManagerService,
    protected adminManagerService: AdminManagerService
  ) { }


  ngOnInit(): void {
  }

  getItemName(): string {
    return this.selectedItem?.DocumentItemName;

    this.selectedItem.ParentDocumentFolderId
  }

  titleChanged() {
    this.titleUpdated.emit(this.selectedItem?.DocumentItemName);
  }

  fileUploadStarted() {
    this.fileUploadHasStarted = true;
  }

  fileUploadComplete(fileUploadModalResult: DwFileUploadModalResult) {
    this.fileUploadHasStarted = false;
    this.fileUploadResult = fileUploadModalResult;

    if (fileUploadModalResult && fileUploadModalResult.media && fileUploadModalResult.media.length > 0) {
      this.newMedia = fileUploadModalResult.media[0];

      this.selectedItem.MediaId = this.newMedia.MediaId;

      if (this.selectedItem.DocumentItemName == 'New Document') {
        this.selectedItem.DocumentItemName = this.newMedia.MediaName || 'New Document';
        this.titleUpdated.emit(this.selectedItem?.DocumentItemName);
      }
    }
  }

  isFileValidChange(isValid: boolean) {
    this.isFileValid = isValid;
  }

  validateSave(): SheetValidationResult {
    const validationResult : SheetValidationResult = {
      isValid: true
    }

    const messageParts: string[] = [];

    if(this.isFileValid === false) {
      messageParts.push('The selected file is not valid.');
    } else {
      if (this.adding && !this.selectedItem.MediaId) {
        messageParts.push('A file must be uploaded for the new document.');
      }
    }

    if(this.fileUploadHasStarted) {
      messageParts.push('File upload is still in progress. Please wait for it to complete before saving.');
    }

    if (this.selectedItem.DocumentItemName == null || this.selectedItem.DocumentItemName.trim() === '') {
      messageParts.push('The document must have a name.');
    }
    
    if (messageParts.length > 0) {
      validationResult.message = messageParts.join(' ');
      validationResult.isValid = false;
    }

    return validationResult;
  }

  initDocumentData(documentCardData: DocumentCardData): Observable<DocumentSetupResult> {
    this.selectedItem = null;

    this.documentCardData = documentCardData;

    this.adding = false;

    if (documentCardData.newItem) {
      return this.setupAddForm();
    }

    if (!documentCardData?.documentItem) {
      return of(null);
    }

    return this.documentRespositoryService.loadDocumentItem(documentCardData.documentItem.DocumentItemId).pipe(map(documentItem => {
      this.selectedItem = documentItem;

      const result: DocumentSetupResult = {
        ...this.setupDefaults,
        initialTitle: this.selectedItem?.DocumentItemName || '',
        changeLog: documentItem.DocumentItemChangeLog || []
      };

      return result;
    }));
  }

  setupAddForm(): Observable<DocumentSetupResult> {
    const result: DocumentSetupResult = {
      ...this.setupDefaults,
      startInEditMode: true,
      initialTitle: 'New Document',
      hideChangeLog: true,
      hideDelete: true,
      hideEditGate: true,
      hideChangeNotes: true,
      changeLog: []
    };

    this.adding = true;
    this.selectedItem = AppMetaData.DocumentItem.CreateEntity();

    const aviatorUser = this.adminManagerService.getAviatorUser();

    this.selectedItem.FlightSchoolId = aviatorUser?.FlightSchoolId || null;
    this.selectedItem.DocumentItemStatusId = DocumentItemStatusAllItems.Active;
    this.selectedItem.ParentDocumentFolderId = this.documentCardData?.parentFolderId || null;
    this.selectedItem.UseCustomCardIcon = false;
    this.selectedItem.DocumentGroupId = this.documentsManagerService.determineDocumentGroup();
    this.selectedItem.DocumentTypeId = DocumentTypeAllItems.PDFDocument;
    this.selectedItem.DocumentOwnershipTypeId = DocumentOwnerTypeAllItems.FlightSchool;
    this.selectedItem.DocumentItemName = 'New Document';
    this.selectedItem.RequireStudentAcknowledgement = false;

    return of(result);
  }

  deleteItemInternal(): Observable<DocumentDataUpdateInfo> {
    if (!this.selectedItem) return;

    this.selectedItem.DocumentItemStatusId = DocumentItemStatusAllItems.Inactive;

    return this.documentRespositoryService.saveDocumentItem(this.selectedItem)
      .pipe(map((data) => {
        return { data: this.selectedItem, action: 'delete', DocumentCardData: this.documentCardData };
      }));
  }

  saveChangesInternal(changeDescription: string): Observable<DocumentDataUpdateInfo> {

    return this.buildChangeLog(this.selectedItem, false, changeDescription).pipe(
      mergeMap(itemToSave => {
        return this.documentRespositoryService.saveDocumentItem(itemToSave)
    })
    ).pipe(map((data) => {
      this.selectedItem = data;
      return { data: data, action: 'update', DocumentCardData: this.documentCardData };
    }));
  }

  protected buildChangeLog(documentItemEntity: DocumentItemEntity, isDelete: boolean, changeDescription: string): Observable<DocumentItemEntity> {
    const itemToSave = { ...documentItemEntity };
    itemToSave.DocumentItemChangeLog = [];

    let changeLog = AppMetaData.DocumentItemChangeLog.CreateEntity();

    changeLog.DocumentItemId = this.selectedItem.DocumentItemId;
    changeLog.ChangeLogText = changeDescription;

    if (this.adding) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Renamed;

      changeLog.ChangeLogText = `Document Created`

      itemToSave.DocumentItemChangeLog = [...itemToSave.DocumentItemChangeLog, changeLog];

      return of(itemToSave);
    }

    if (isDelete) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Deleted;
      changeLog.ChangeLogText = `File Deleted`;

      itemToSave.DocumentItemChangeLog = [changeLog];

      return of(itemToSave);
    }

    if (this.fieldChanged('DocumentItemName')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Renamed;

      changeLog.ChangeLogText = `Renamed from "${this.documentCardData?.documentItem?.DocumentItemName}" to "${this.selectedItem.DocumentItemName}". ` + changeLog.ChangeLogText;

      itemToSave.DocumentItemChangeLog = [...itemToSave.DocumentItemChangeLog, changeLog];

      changeLog = { ...changeLog };
    }

    if (this.fieldChanged('RequireStudentAcknowledgement')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.AcknowledgementChanged;

      if (this.selectedItem.RequireStudentAcknowledgement) {
        changeLog.ChangeLogText = `Set to require student acknowledgement. ` + changeLog.ChangeLogText;
      } else {
        changeLog.ChangeLogText = `Set to not require student acknowledgement. ` + changeLog.ChangeLogText;
      }
      
      itemToSave.DocumentItemChangeLog = [...itemToSave.DocumentItemChangeLog, changeLog];

      changeLog = { ...changeLog };
    }

    if (this.fieldChanged('MediaId')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.FileReplaced;

      changeLog.ChangeLogText = `File replaced with newer version.` + changeLog.ChangeLogText;;

      changeLog.MediaId = this.documentCardData?.documentItem?.MediaId;
      itemToSave.DocumentItemChangeLog = [...itemToSave.DocumentItemChangeLog, changeLog];

      changeLog = { ...changeLog, MediaId: null };
    }

    if (this.fieldChanged('ParentDocumentFolderId')) {
      changeLog.ChangeLogTypeId = DocumentChangeLogTypeAllItems.Moved;

      return this.documentRespositoryService.getDocumentFoldersFromTo(itemToSave.DocumentGroupId, this.documentCardData?.documentItem?.ParentDocumentFolderId, this.selectedItem.ParentDocumentFolderId).pipe(map(folderNames => {
        changeLog.ChangeLogText = `Moved from ${folderNames}. ` + changeLog.ChangeLogText;

        itemToSave.DocumentItemChangeLog = [...itemToSave.DocumentItemChangeLog, changeLog];

        return itemToSave;
      }));
    }


    return of(itemToSave);
  }


  private fieldChanged(fieldName: keyof DocumentItemEntity): boolean {
    const originalValue = this.documentCardData?.documentItem ? this.documentCardData.documentItem[fieldName] : null;
    const currentValue = this.selectedItem ? this.selectedItem[fieldName] : null;

    return originalValue !== currentValue;
  }

  hasChanges(): boolean {
    // New items always count as having changes
    if (this.adding) {
      return true;
    }

    if (this.fileUploadHasStarted) {
      return false;
    }

    return this.fieldChanged('DocumentItemName') ||
           this.fieldChanged('ParentDocumentFolderId') ||
           this.fieldChanged('MediaId') ||
           this.fieldChanged('RequireStudentAcknowledgement');
  }

}
